/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { useMemo } from 'react';

import { jsx } from '@atlaskit/css';
import { Stack } from '@atlaskit/primitives/compiled';

import type { WorkViewProps } from './types';
import type { WorkGroup as WorkGroupType } from './types';
import { WorkGroup } from './WorkGroup';

export function WorkView({
	actionsByIndex,
	hydratedUsers,
	workById,
	selectedKeys,
	onSelectionChange,
	onConfirm,
	onCancel,
	getStatus,
	expandAllGroups = false,
}: WorkViewProps): React.JSX.Element {
	const workGroups = useMemo<WorkGroupType[]>(() => {
		const groups: WorkGroupType[] = Array.from(actionsByIndex.actionsByWorkId.entries()).map(
			([workId, actions]) => ({
				workId,
				actions,
			}),
		);

		// Collect actions that have no associated work ID (e.g. ADD_CONTRIBUTORS / REMOVE_CONTRIBUTORS)
		const actionsWithWork = new Set<string>(
			Array.from(actionsByIndex.actionsByWorkId.values())
				.flat()
				.map((a) => a.invocationId),
		);
		const actionsWithoutWork = Array.from(actionsByIndex.actionsByContributorId.values())
			.flat()
			.filter((a) => !actionsWithWork.has(a.invocationId));

		if (actionsWithoutWork.length > 0) {
			groups.push({ workId: null, actions: actionsWithoutWork });
		}

		return groups;
	}, [actionsByIndex]);

	return (
		<Stack>
			{workGroups.map((group, index) => (
				<WorkGroup
					key={group.workId ?? '__no_work__'}
					group={group}
					defaultExpanded={expandAllGroups || (group.defaultExpanded ?? index === 0)}
					selectedKeys={selectedKeys}
					onSelectionChange={onSelectionChange}
					workById={workById}
					hydratedUsers={hydratedUsers}
					onConfirm={onConfirm}
					onCancel={onCancel}
					getStatus={getStatus}
				/>
			))}
		</Stack>
	);
}
